<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { OUTPUTS } from "@/store/modules/output";
import debounce from "@/utils/debounce";

const store = useStore();

const makes = computed(() => store.state.makes.list);
const loading = computed(() => store.state.makes.loading);
const error = computed(() => store.state.makes.error);
const prompt = computed(() => store.state.prompt);

const search = ref(store.state.makes.search);
const renaming = ref("");
const newTitle = ref("");
const confirmingDelete = ref("");
const titleInput = ref(null);

const OUTPUT_DETAILS = Object.fromEntries(
  OUTPUTS.map((option) => [option.id, option])
);
const outputOf = (make) => OUTPUT_DETAILS[make.output] || OUTPUT_DETAILS.text;
const when = (date) =>
  new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const fetchList = (value) => store.dispatch("makes/fetchList", value);
const runSearch = debounce(() => fetchList(search.value), 300);

onMounted(() => fetchList(search.value));

const back = () => {
  store.dispatch(prompt.value ? "drawer/createMake" : "drawer/reset");
};

const open = (id) => store.dispatch("makes/open", id);

const startRename = async (make) => {
  renaming.value = make.id;
  newTitle.value = make.title;
  await nextTick();
  titleInput.value?.[0]?.focus();
};
const saveRename = async (make) => {
  const title = newTitle.value.trim();
  renaming.value = "";
  if (title && title !== make.title) {
    await store.dispatch("makes/rename", { id: make.id, title });
  }
};
const remove = async (id) => {
  confirmingDelete.value = "";
  await store.dispatch("makes/remove", id);
};
</script>

<template>
  <section class="mt-8 mb-32 lg:mt-12">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <button
        type="button"
        class="flex items-center text-sm font-bold uppercase text-accentPrimary"
        @click="back"
      >
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-2" />
        Back
      </button>
      <h2 class="text-2xl font-bold text-primary lg:text-3xl">My makes</h2>
    </div>
    <label for="makes-search" class="sr-only">Search your makes</label>
    <input
      id="makes-search"
      v-model="search"
      type="search"
      placeholder="Search your makes..."
      class="mt-6 w-full rounded-lg border border-gray-200 px-4 py-3 text-primary outline-none focus:border-accentPrimary"
      @input="runSearch"
    />
    <p v-if="error" class="mt-4 text-sm font-medium text-red-500">
      {{ error }}
    </p>
    <p
      v-else-if="!loading && !makes.length"
      class="mt-8 text-center text-secondary"
    >
      {{
        search
          ? "No makes match that search."
          : "Your makes will be kept here once you've made something."
      }}
    </p>
    <ul
      class="mt-6 grid gap-4 md:grid-cols-2"
      :class="{ 'opacity-60': loading }"
    >
      <li
        v-for="make in makes"
        :key="make.id"
        class="flex flex-col rounded-lg border border-gray-200 p-4 text-primary"
      >
        <div class="flex items-start justify-between gap-3">
          <input
            v-if="renaming === make.id"
            ref="titleInput"
            v-model="newTitle"
            :aria-label="`New title for ${make.title}`"
            class="min-w-0 flex-1 rounded border border-accentPrimary px-2 py-1 font-bold outline-none"
            @keyup.enter="saveRename(make)"
            @keyup.esc="renaming = ''"
            @blur="saveRename(make)"
          />
          <button
            v-else
            type="button"
            class="min-w-0 text-left text-lg font-bold hover:text-accentPrimary"
            @click="open(make.id)"
          >
            {{ make.title }}
          </button>
          <span
            class="flex shrink-0 items-center rounded-full bg-gray-10 px-2 py-0.5 text-xs font-medium text-secondary"
          >
            <font-awesome-icon
              :icon="['fas', outputOf(make).icon]"
              class="mr-1"
            />
            {{ outputOf(make).label }}
          </span>
        </div>
        <p class="mt-1 text-xs text-secondary">
          {{ when(make.updatedAt) }}
          <span
            v-if="make.shared"
            class="ml-2 rounded-full bg-accentSecondaryLight px-2 py-0.5 font-medium text-primary"
            >Shared</span
          >
        </p>
        <p class="mt-2 line-clamp-2 text-sm text-secondary">
          {{ make.preview }}
        </p>
        <div class="mt-auto flex items-center gap-4 pt-3 text-sm font-bold">
          <button
            type="button"
            class="uppercase text-accentPrimary hover:text-purple-400"
            @click="open(make.id)"
          >
            Open
          </button>
          <button
            type="button"
            class="uppercase text-secondary hover:text-primary"
            @click="startRename(make)"
          >
            Rename
          </button>
          <button
            v-if="make.shared"
            type="button"
            class="uppercase text-secondary hover:text-primary"
            @click="store.dispatch('makes/unshare', make.id)"
          >
            Stop sharing
          </button>
          <template v-if="confirmingDelete === make.id">
            <button
              type="button"
              class="uppercase text-red-500 hover:text-red-700"
              @click="remove(make.id)"
            >
              Delete it
            </button>
            <button
              type="button"
              class="uppercase text-secondary hover:text-primary"
              @click="confirmingDelete = ''"
            >
              Keep
            </button>
          </template>
          <button
            v-else
            type="button"
            class="uppercase text-secondary hover:text-red-600"
            @click="confirmingDelete = make.id"
          >
            Delete
          </button>
        </div>
      </li>
    </ul>
  </section>
</template>
